import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LineChart } from '@mui/x-charts/LineChart';
import styles from './styles.module.css';

const SERIES_COLORS = ['#10B2D0', '#095664', '#98D0DA'];

const LineChartCard = ({ data, noCard }) => {
  const chartWrapRef = useRef(null);

  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const roundMarks = () => {
      const marks = wrap.querySelectorAll('.MuiChartsLegend-mark');
      marks.forEach((rect) => {
        const w = rect.getAttribute('width');
        const h = rect.getAttribute('height');
        if (w && h) {
          const r = Math.min(Number(w), Number(h)) / 2;
          rect.setAttribute('rx', r);
          rect.setAttribute('ry', r);
        }
      });
    };
    const id = requestAnimationFrame(roundMarks);
    return () => cancelAnimationFrame(id);
  }, [data]);

  const chartData = useMemo(() => {
    const raw = Array.isArray(data) ? data : [];
    const timeAxis = raw.map((d) => d.date_of_month);
    const sales = raw.map((d) => Number(d.date_sales) || 0);
    const cost = raw.map((d) => Number(d.date_cost) || 0);
    const profit = raw.map((d) => Number(d.date_profit) || 0);
    return {
      timeAxis,
      sales,
      cost,
      profit,
    };
  }, [data]);

  const { timeAxis, sales, cost, profit } = chartData;
  const hasData = timeAxis.length > 0;

  const chartContent = (
    <div ref={chartWrapRef} className={styles.chartWrap}>
      {!hasData ? (
        <div className={styles.empty}>No data</div>
      ) : (
        <LineChart
          className={styles.chart}
          xAxis={[
            {
              id: 'days',
              data: timeAxis,
              scaleType: 'point',
            },
          ]}
          series={[
            { id: 'sales', label: 'Sales', data: sales, color: SERIES_COLORS[0], showMark: false },
            { id: 'cost', label: 'Cost', data: cost, color: SERIES_COLORS[1], showMark: false },
            { id: 'profit', label: 'Profit', data: profit, color: SERIES_COLORS[2], showMark: false },
          ]}
          height={260}
          margin={{ top: 16, right: 16, bottom: 75, left: 48 }}
          grid={{ horizontal: true }}
          aria-label="Sales, cost, and profit over time"
          disableLineItemHighlight
          disableAxisListener
          slotProps={{
            legend: {
              position: { vertical: 'bottom', horizontal: 'middle' },
              direction: 'row',
              padding: { top: 16, bottom: 0, left: 0, right: 0 },
            },
          }}
          sx={{
            '& .MuiChartsAxis-tickLabel': {
              fill: 'var(--gray-600)',
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-sm)',
            },
            '& .MuiChartsAxis-line': {
              stroke: 'var(--gray-200)',
            },
            '& .MuiChartsAxis-tick': {
              stroke: 'var(--gray-200)',
            },
            '& .MuiChartsGrid-line': {
              stroke: 'var(--gray-200)',
            },
            '& .MuiChartsLegend-label': {
              fill: 'var(--black)',
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-sm)',
            },
            '& .MuiChartsLegend-root text': {
              fill: 'var(--black)',
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-sm)',
            },
            '& .MuiChartsLegend-root tspan': {
              fill: 'var(--black)',
            },
            '& .MuiLineElement-root': {
              strokeWidth: 2,
            },
          }}
        />
      )}
    </div>
  );

  if (noCard) return chartContent;
  return <div className={styles.card}>{chartContent}</div>;
};

LineChartCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  noCard: PropTypes.bool,
};

LineChartCard.defaultProps = {
  data: [],
  noCard: false,
};

export default LineChartCard;
